import * as yup from 'yup';
import {
    AggregationType,
    BarOrientation,
    BarSortOrder,
    Chart,
    ChartFieldToQueryOption,
    ChartIntervalUnit,
    ChartOutput,
    ChartQuery,
    DataLabelPosition,
} from 'entities/chart';
import {chartQueryDefaultProps} from 'views/storyboard/utils/chartPropTypes';
import {ChartFilterType, chartHeightValues, ChartType, chartWidthValues} from 'views/storyboard/utils/chartConstants';
import {Parser} from 'expr-eval';
import i18n from 'i18n';

// @ts-ignore
export const chartQueryFormSchema: yup.Schema<Omit<ChartQuery, 'id' | 'limit'>> = yup.object({
    query_string: yup.string().required(i18n.t('ch_search_query_required.text')),
    fields_to_query: yup
        .mixed<ChartFieldToQueryOption>()
        // TODO feedback text samples text field query options
        // .oneOf<ChartFieldToQueryOption>(Object.values(ChartFieldToQueryOption) as Array<ChartFieldToQueryOption>)
        .required(),
    output: yup.mixed<ChartOutput>().oneOf<ChartOutput>(Object.values(ChartOutput)).nullable(),
    // @ts-ignore
    aggs_item: yup.string().nullable(true).default(chartQueryDefaultProps.aggs_item),
    aggs_item_list: yup.array().of(
        yup.object({
            value: yup.string().required(),
            label: yup.string().required(),
        }).required(),
    ).default(chartQueryDefaultProps.aggs_item_list),
    intervalUnit: yup.mixed<ChartIntervalUnit>().oneOf<ChartIntervalUnit>(Object.values(ChartIntervalUnit)).required(),
    intervalNumber: yup
        .number()
        .min(1, i18n.t('ch_value_positive_number.text'))
        .default(chartQueryDefaultProps.intervalNumber),
    metric_timeline_type: yup
        .mixed<AggregationType>()
        .oneOf(Object.values(AggregationType))
        .default(chartQueryDefaultProps.metric_timeline_type),
    metric_aggregations_type: yup
        .mixed<AggregationType>()
        .oneOf(Object.values(AggregationType))
        .default(chartQueryDefaultProps.metric_timeline_type),
    metric_aggregations_aggs_item: yup.string().nullable().default(chartQueryDefaultProps.metric_aggregations_aggs_item),
    pivot_col_aggs_item: yup.string().nullable().default(chartQueryDefaultProps.pivot_col_aggs_item),
    metric_interval: yup.number().default(chartQueryDefaultProps.metric_interval),

    concordance_query: yup.string().default(chartQueryDefaultProps.concordance_query),
    concordance_width: yup.number().default(chartQueryDefaultProps.concordance_width),
    concordance_lines: yup.number().default(chartQueryDefaultProps.concordance_lines),

    trending_or_disappearing: yup.string().default(chartQueryDefaultProps.trending_or_disappearing),
    trending_comparable_period: yup.number().default(chartQueryDefaultProps.trending_comparable_period),
    trending_period: yup.string().default(chartQueryDefaultProps.trending_period),
    nested_terms_include_missing: yup.bool().default(chartQueryDefaultProps.nested_terms_include_missing),

    wordcloud_most_common_words: yup.number(),
    wordcloud_text_start_point: yup.number(),
    wordcloud_text_end_point: yup.number(),
    // Word cloud params
    n_gram: yup.number().default(chartQueryDefaultProps.n_gram),
    filtering_lists: yup.array().of(
        yup.object({
            value: yup.number().required(),
            label: yup.string().required(),
        }).required(),
    ),

    all_fields: yup.lazy((value: any) => {
        if (Array.isArray(value)) {
            return yup.array().of(
                yup.object({
                    value: yup.string().required(),
                    label: yup.string(),
                }).required()
            );
        }

        if (typeof value === 'object' && value !== null) {
            const nestedFields: Record<string, yup.Schema<any>> = {};

            Object.keys(value).forEach(key => {
                // @ts-ignore
                nestedFields[key] = yup.lazy(() => {
                    const fieldValue = value[key];
                    if (Array.isArray(fieldValue)) {
                        return yup.array().of(
                            yup.object({
                                value: yup.string().required(),
                                label: yup.string(),
                            }).required()
                        );
                    } else if (typeof fieldValue === 'object' && fieldValue !== null) {
                        return yup.object().shape({});
                    } else {
                        return yup.string().required();
                    }
                });
            });

            return yup.object(nestedFields).required();
        }

        return yup.string().required();
    }),

    additionalFilters: yup.array().of(yup.object({
        type: yup.mixed<ChartFilterType>().oneOf(Object.values(ChartFilterType)).required(),
        label: yup.string(),
        value: yup.string().required(),
        description: yup.string().nullable(),
        label_clean: yup.string(),
        use_case: yup.string().nullable(),
        scale: yup.string().nullable(),
        event_type: yup.string().nullable(),
    })).required(),

    additionalData: yup.object({
        comment: yup.string().default(chartQueryDefaultProps.additionalData.comment),
    }).default(chartQueryDefaultProps.additionalData),
}).required();


yup.addMethod<yup.StringSchema>(yup.string, 'scalingFactor', function (errorMessage: string) {
    return this.test('test-is-valid-scaling-factor', errorMessage, function (value: string | undefined) {
        const {path, createError} = this;

        const parser = new Parser();
        let isValid: boolean;
        try {
            if (value) {
                parser.parse(`count * (${value})`);
            }
            isValid = true;
        } catch {
            isValid = false;
        }

        return (
            isValid ||
            createError({path, message: errorMessage})
        );
    });
});

// @ts-ignore
export const chartSettingsFormSchema: yup.Schema<Omit<Chart, 'chartQuery' | 'id'>> = yup.object({
    type: yup.mixed<ChartType>()
        .oneOf<ChartType>(Object.values(ChartType))
        .required(),
    width: yup.number().oneOf(chartWidthValues).required(),
    height: yup.number().oneOf(chartHeightValues).required(),
    title: yup.string().max(255, i18n.t('ch_cannot_be_longer.text')).required(),
    description: yup
        .string()
        .max(512, i18n.t('chart_description_cannot_be_longer.text'))
        .default(''),
    linearRegression: yup.bool().required(),
    comparablePeriod: yup.bool().required(),
    movingAverageDays: yup
        .number()
        .min(1, i18n.t('ch_moving_avg_has_to_be_positive_integer.text'))
        // @ts-ignore
        .nullable(true)
        .default(null),
    movingAverageDaysCenter: yup.bool().required().default(true),
    scalingFactor: yup
        .string()
        .max(255, i18n.t('ch_cannot_be_longer.text'))
        .scalingFactor(i18n.t('ch_invalid_expression.text'))
        .default(''),
    barOrientation: yup
        .mixed<BarOrientation>()
        .oneOf(Object.values(BarOrientation))
        .default(BarOrientation.Vertical),
    barSortOrder: yup
        .mixed<BarSortOrder>()
        .oneOf(Object.values(BarSortOrder))
        .default(BarSortOrder.HighToLow),
    dataLabelPosition: yup
        .mixed<DataLabelPosition>()
        .oneOf(Object.values(DataLabelPosition))
        .default(DataLabelPosition.Off)
        .required(),
    // NOTE because of a bug in yup this transform isn't actually working. It should be fixed in the next stable (1.0.0)
    //      version but currently is not causing too many problems here I think
    storyboardId: yup.number().integer().required().transform(value => parseInt(value, 10)),
}).required();
